// @flow
import { getJson, postJson, putJson, del } from 'utils/http';
import type {
  MiniPage,
  SplitContentBlock,
  SplitContentBlockData
} from 'api/cms';

export type ScheduledContentBlock = SplitContentBlock;

export type ScheduledContent = {
  id: string,
  title: string,
  note: string,
  starts: string,
  contentBlocks: Array<ScheduledContentBlock>
};

export type ScheduledContentInfo = {
  note: string,
  starts: string,
  contentBlocks: Array<ScheduledContentBlock>
};

export type ScheduledContentQueueMini = {
  id: string,
  name: string
};

export type ScheduledContentQueue = {
  id: string,
  name: string,
  usedOnPages: Array<MiniPage>,
  scheduledContents: Array<ScheduledContent>
};

export type ScheduledContentQueueInfo = {
  id: string,
  name: string
};

/* Get the list of all queues: */
export function fetchScheduledContentQueues(): Promise<
  Array<ScheduledContentQueueMini>
> {
  return getJson('/data/cms/scheduled-content-queues').then(
    ({ scheduledContentQueues }) => scheduledContentQueues
  );
}

/* Create a new queue: */
export function createScheduledContentQueue(
  scheduledContentQueueInfo: ScheduledContentQueueInfo
): Promise<ScheduledContentQueue> {
  return postJson(
    '/data/cms/scheduled-content-queues',
    scheduledContentQueueInfo
  ).then(({ scheduledContentQueue }) => scheduledContentQueue);
}

/* Get a queue: */
export function fetchScheduledContentQueue(
  queueId: string
): Promise<ScheduledContentQueue> {
  return getJson(`/data/cms/scheduled-content-queues/${queueId}`).then(
    ({ scheduledContentQueue }) => scheduledContentQueue
  );
}

/* Edit a queue: */
export function updateScheduledContentQueue(
  scheduledContentQueueInfo: ScheduledContentQueueInfo
): Promise<ScheduledContentQueue> {
  return putJson(
    `/data/cms/scheduled-content-queues/${scheduledContentQueueInfo.id}`,
    scheduledContentQueueInfo
  ).then(({ scheduledContentQueue }) => scheduledContentQueue);
}

/* NOTE(arnav): This is currently not in use. */
/* Delete a queue: */
export function deleteScheduledContentQueue(
  queueId: string
): Promise<ScheduledContentQueue> {
  return del(`/data/cms/scheduled-content-queues/${queueId}`).then(
    ({ scheduledContentQueue }) => scheduledContentQueue
  );
}

/* Add a scheduled content to a queue: */
export function createScheduledContent(
  queueId: string,
  scheduledContentInfo: ScheduledContentInfo
): Promise<ScheduledContent> {
  return postJson(
    `/data/cms/scheduled-content-queues/${queueId}/scheduled-contents`,
    scheduledContentInfo
  ).then(({ scheduledContent }) => scheduledContent);
}

/* Get a scheduled content: */
export function fetchScheduledContent(
  scheduledContentId: string
): Promise<ScheduledContent> {
  return getJson(`/data/cms/scheduled-contents/${scheduledContentId}`).then(
    ({ scheduledContent }) => scheduledContent
  );
}

/* Edit a scheduled content: */
export function updateScheduledContent(
  scheduledContentId: string,
  scheduledContentInfo: ScheduledContentInfo
): Promise<ScheduledContent> {
  return putJson(
    `/data/cms/scheduled-contents/${scheduledContentId}`,
    scheduledContentInfo
  ).then(({ scheduledContent }) => scheduledContent);
}

/* Delete a scheduled content: */
export function deleteScheduledContent(
  scheduledContentId: string
): Promise<ScheduledContent> {
  return del(`/data/cms/scheduled-contents/${scheduledContentId}`).then(
    ({ scheduledContent }) => scheduledContent
  );
}

/* Get a scheduled content preview: */
export function fetchScheduledContentPreview(
  scheduledContentId: string
): Promise<Array<{ data: SplitContentBlockData }>> {
  return getJson(
    `/data/cms/scheduled-contents/${scheduledContentId}/preview`
  ).then(({ contentBlocks }) => contentBlocks);
}
